import React, { useState, useEffect } from 'react';
import { localDate } from '../utils/dates';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, MenuItem, Select, FormControl, InputLabel,
  Chip, Card, CardContent, InputAdornment, Alert, Snackbar, Avatar,
  TablePagination, Tooltip, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Person as PersonIcon, Visibility as ViewIcon,
  Close as CloseIcon, School as SchoolIcon, FileDownload as DownloadIcon,
  CloudUpload as UploadIcon, CameraAlt as CameraIcon, VpnKey as KeyIcon
} from '@mui/icons-material';
import { studentAPI, academicAPI, feeAPI } from '../services/api';
// casteAPI is part of academicAPI
import * as XLSX from 'xlsx';

const initialFormState = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '', bloodGroup: '',
  address: '', phone: '', email: '', rollNo: '', schoolClass: null, section: null,
  admissionDate: localDate(),
  academicYearId: '',
  aadharNumber: '', academicYearFees: '',
  caste: '', subCaste: '', category: '',
  photo: '',
  // Guardian Info
  fatherName: '', fatherPhone: '', fatherEmail: '', fatherOccupation: '',
  motherName: '', motherPhone: '', motherEmail: '', motherOccupation: '',
  address2: '', emergencyContact: '',
  // Student's own app login
  studentUsername: '', studentPassword: '',
  // Guardian App Login Credentials (optional)
  parentUsername: '', parentPassword: '', parentFullName: '', parentPhone: '', parentEmail: ''
};

export default function StudentManagement() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [activeAcademicYearId, setActiveAcademicYearId] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loginDialog, setLoginDialog] = useState(null); // { student, username, password }
  const [formData, setFormData] = useState(initialFormState);
  const [isEdit, setIsEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterClass, setFilterClass] = useState('');
  const [ageError, setAgeError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [aadharError, setAadharError] = useState('');
  const [casteList, setCasteList] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [autoFeeInfo, setAutoFeeInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.schoolClass) {
      const filtered = allSections.filter(s => s.schoolClass?.id === parseInt(formData.schoolClass));
      setSections(filtered);
    } else {
      setSections([]);
    }
  }, [formData.schoolClass, allSections]);

  useEffect(() => {
    if (formData.schoolClass && formData.academicYearId && feeStructures.length > 0) {
      const structure = feeStructures.find(fs =>
        String(fs.schoolClass?.id) === String(formData.schoolClass) &&
        String(fs.academicYear?.id) === String(formData.academicYearId)
      );
      if (structure && structure.totalFee) {
        setFormData(prev => ({ ...prev, academicYearFees: String(structure.totalFee) }));
        setAutoFeeInfo(structure);
      } else {
        setAutoFeeInfo(null);
      }
    } else {
      setAutoFeeInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.schoolClass, formData.academicYearId, feeStructures]);

  const handleDobChange = (e) => {
    const dob = e.target.value;
    setFormData({...formData, dateOfBirth: dob});
    if (dob) {
      const today = new Date();
      const birth = new Date(dob);
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      setAgeError(age < 16 ? 'Student must be at least 16 years old' : '');
    } else {
      setAgeError('');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [studentsRes, classesRes, sectionsRes, yearsRes, castesRes, feesRes] = await Promise.all([
        studentAPI.getAll(),
        academicAPI.getClasses(),
        academicAPI.getSections(),
        academicAPI.getYears().catch(() => ({ data: [] })),
        academicAPI.getCastes().catch(() => ({ data: [] })),
        feeAPI.getStructures().catch(() => ({ data: [] })),
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
      setAllSections(sectionsRes.data);
      setCasteList(castesRes.data || []);
      setFeeStructures(feesRes.data || []);
      const years = yearsRes.data || [];
      setAcademicYears(years);
      const activeYear = years.find(y => y.isActive) || years[0] || null;
      const activeYearId = activeYear ? activeYear.id : null;
      setActiveAcademicYearId(activeYearId);
      setFormData(prev => ({ ...prev, academicYearId: prev.academicYearId || activeYearId || '' }));
    } catch (error) {
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (student = null) => {
    setAgeError(''); setPhoneError(''); setAadharError('');
    if (student) {
      setIsEdit(true);
      setFormData({
        ...initialFormState,
        id: student.id,
        firstName: student.firstName || '',
        lastName: student.lastName || '',
        dateOfBirth: student.dateOfBirth || '',
        gender: student.gender || '',
        bloodGroup: student.bloodGroup || '',
        address: student.address || '',
        phone: student.phone || '',
        email: student.email || '',
        rollNo: student.rollNo || '',
        admissionDate: student.admissionDate || '',
        schoolClass: student.schoolClass?.id || '',
        section: student.section?.id || '',
        academicYearId: student.academicYear?.id || activeAcademicYearId || '',
        aadharNumber: student.aadharNumber || '',
        academicYearFees: student.academicYearFees !== null && student.academicYearFees !== undefined ? String(student.academicYearFees) : '',
        caste: student.caste || '',
        subCaste: student.subCaste || '',
        category: student.category || '',
        photo: student.photo || '',
      });
    } else {
      setIsEdit(false);
      setFormData({ ...initialFormState, academicYearId: activeAcademicYearId || '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(initialFormState);
    setIsEdit(false);
    setAgeError(''); setPhoneError(''); setAadharError('');
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, phone: val });
    if (val && val.length !== 10) setPhoneError('Phone number must be exactly 10 digits');
    else setPhoneError('');
  };

  const handleAadharChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, aadharNumber: val });
    if (val && val.length !== 12) setAadharError('Aadhaar number must be exactly 12 digits');
    else setAadharError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showSnackbar('Please select a valid image file', 'error'); return; }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const maxSize = 200;
      let w = img.width, h = img.height;
      if (w > h) { if (w > maxSize) { h = (h * maxSize) / w; w = maxSize; } }
      else { if (h > maxSize) { w = (w * maxSize) / h; h = maxSize; } }
      canvas.width = w; canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
      const base64 = canvas.toDataURL('image/jpeg', 0.85);
      setFormData(prev => ({ ...prev, photo: base64 }));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setOpenViewDialog(true);
  };

  const handleDeleteClick = (student) => {
    setSelectedStudent(student);
    setOpenDeleteDialog(true);
  };

  const handleSubmit = async () => {
    if (ageError) { showSnackbar(ageError, 'error'); return; }
    if (formData.phone && formData.phone.replace(/\D/g, '').length !== 10) { showSnackbar('Phone number must be exactly 10 digits', 'error'); return; }
    if (formData.aadharNumber && formData.aadharNumber.replace(/\D/g, '').length !== 12) { showSnackbar('Aadhaar number must be exactly 12 digits', 'error'); return; }
    if (!isEdit && (formData.studentUsername || formData.studentPassword)
        && (!formData.studentUsername.trim() || formData.studentPassword.length < 6)) {
      showSnackbar('Student login needs a username and a password of at least 6 characters', 'error'); return;
    }
    try {
      if (isEdit) {
        // Build clean Student update payload — only the fields PUT endpoint accepts
        const classId = formData.schoolClass ? parseInt(formData.schoolClass) : null;
        const sectionId = formData.section ? parseInt(formData.section) : null;
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth || null,
          gender: formData.gender || null,
          bloodGroup: formData.bloodGroup || null,
          address: formData.address || null,
          phone: formData.phone || null,
          email: formData.email || null,
          rollNo: formData.rollNo || null,
          admissionDate: formData.admissionDate || null,
          schoolClass: classId ? { id: classId } : null,
          section: sectionId ? { id: sectionId } : null,
          aadharNumber: formData.aadharNumber || null,
          academicYearFees: formData.academicYearFees ? parseFloat(formData.academicYearFees) : null,
          caste: formData.caste || null,
          subCaste: formData.subCaste || null,
          category: formData.category || null,
          photo: formData.photo || null,
        };
        await studentAPI.update(formData.id, updateData);
        showSnackbar('Student updated successfully');
      } else {
        // Build clean CreateStudentRequest payload
        const classId = formData.schoolClass ? parseInt(formData.schoolClass) : null;
        const sectionId = formData.section ? parseInt(formData.section) : null;
        const createData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth || null,
          gender: formData.gender || null,
          bloodGroup: formData.bloodGroup || null,
          address: formData.address || null,
          phone: formData.phone || null,
          email: formData.email || null,
          rollNo: formData.rollNo || null,
          admissionDate: formData.admissionDate || null,
          classId: classId,
          sectionId: sectionId,
          academicYearId: formData.academicYearId ? parseInt(formData.academicYearId) : (activeAcademicYearId || null),
          studentUsername: formData.studentUsername || null,
          studentPassword: formData.studentPassword || null,
          parentUsername: formData.parentUsername || null,
          parentPassword: formData.parentPassword || null,
          parentFullName: formData.parentFullName || null,
          parentPhone: formData.parentPhone || null,
          parentEmail: formData.parentEmail || null,
          fatherName: formData.fatherName || null,
          fatherPhone: formData.fatherPhone || null,
          fatherEmail: formData.fatherEmail || null,
          fatherOccupation: formData.fatherOccupation || null,
          motherName: formData.motherName || null,
          motherPhone: formData.motherPhone || null,
          motherEmail: formData.motherEmail || null,
          motherOccupation: formData.motherOccupation || null,
          address2: formData.address2 || null,
          emergencyContact: formData.emergencyContact || null,
          aadharNumber: formData.aadharNumber || null,
          academicYearFees: formData.academicYearFees ? parseFloat(formData.academicYearFees) : null,
          caste: formData.caste || null,
          subCaste: formData.subCaste || null,
          category: formData.category || null,
          photo: formData.photo || null,
        };
        await studentAPI.create(createData);
        showSnackbar(formData.studentUsername
          ? `Admission registered. The student can sign in to the Student app as "${formData.studentUsername}".`
          : 'Admission registered. Use the key icon in the list to create the student\'s app login.');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || 'Error saving student';
      showSnackbar(typeof msg === 'string' ? msg : 'Error saving student', 'error');
    }
  };

  const handleSaveLogin = async () => {
    const { student, username, password } = loginDialog;
    if (!username.trim() || password.length < 6) {
      showSnackbar('Enter a username and a password of at least 6 characters', 'error');
      return;
    }
    try {
      const res = await studentAPI.saveLogin(student.id, username.trim(), password);
      showSnackbar(res.data?.message || 'Student login saved');
      setLoginDialog(null);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Could not save the login', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await studentAPI.delete(selectedStudent.id);
      showSnackbar('Student deleted successfully');
      setOpenDeleteDialog(false);
      loadData();
    } catch (error) {
      showSnackbar('Error deleting student', 'error');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = searchQuery === '' ||
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.admissionNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClass = filterClass === '' || student.schoolClass?.id === parseInt(filterClass);
    return matchesSearch && matchesClass;
  });

  const paginatedStudents = filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const exportToExcel = () => {
    const data = filteredStudents.map(s => ({
      'Admission No': s.admissionNo || '',
      'First Name': s.firstName || '',
      'Last Name': s.lastName || '',
      'Date of Birth': s.dateOfBirth || '',
      'Gender': s.gender || '',
      'Blood Group': s.bloodGroup || '',
      'Program / Year': s.schoolClass?.name || '',
      'Batch': s.section?.name || '',
      'Roll No': s.rollNo || '',
      'Phone': s.phone || '',
      'Email': s.email || '',
      'Address': s.address || '',
      'Admission Date': s.admissionDate || '',
      'Father Name': s.fatherName || '',
      'Father Phone': s.fatherPhone || '',
      'Mother Name': s.motherName || '',
      'Status': s.status || 'Active',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    XLSX.writeFile(wb, `students_export_${localDate()}.xlsx`);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Student Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all students in the school
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 600 }}>
          Admission Student
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #00897b 0%, #004d40 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Students</Typography>
                  <Typography variant="h4" fontWeight="bold">{students.length}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <PersonIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Students</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {students.filter(s => s.active !== false).length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <SchoolIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Programs</Typography>
                  <Typography variant="h4" fontWeight="bold">{classes.length}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <SchoolIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #26c6da 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Batches</Typography>
                  <Typography variant="h4" fontWeight="bold">{allSections.length}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <SchoolIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth placeholder="Search by name, admission no, or email..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Program / Year</InputLabel>
              <Select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} label="Filter by Program / Year">
                <MenuItem value="">All Programs</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredStudents.length} of {students.length}
            </Typography>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportToExcel}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Export Excel
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Students Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Admission No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Student Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Program / Year</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Batch</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No students found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedStudents.map((student, index) => (
                  <TableRow key={student.id} sx={{ '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: index % 2 === 0 ? 'grey.50' : 'white' }}>
                    <TableCell>
                      <Chip label={student.admissionNo} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={student.photo || undefined} sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 14 }}>
                          {!student.photo && `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {student.firstName} {student.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{student.schoolClass?.name || '-'}</TableCell>
                    <TableCell>{student.section?.name || '-'}</TableCell>
                    <TableCell>{student.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip label={student.active !== false ? 'Active' : 'Inactive'}
                        color={student.active !== false ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View"><IconButton size="small" onClick={() => handleViewStudent(student)}>
                        <ViewIcon fontSize="small" />
                      </IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleOpenDialog(student)}>
                        <EditIcon fontSize="small" />
                      </IconButton></Tooltip>
                      <Tooltip title="Student app login"><IconButton size="small" color="secondary"
                        onClick={() => setLoginDialog({ student, username: student.admissionNo || '', password: '' })}>
                        <KeyIcon fontSize="small" />
                      </IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteClick(student)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredStudents.length} page={page}
          onPageChange={(e, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{isEdit ? 'Edit Student' : 'New Student Admission'}</Typography>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Photo Upload */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #ccc' }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={formData.photo || undefined}
                    sx={{ width: 90, height: 90, bgcolor: 'primary.main', fontSize: 32, border: '3px solid #00897b' }}
                  >
                    {!formData.photo && <CameraIcon sx={{ fontSize: 36 }} />}
                  </Avatar>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>Student Photo</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Upload a passport-size photo. JPG/PNG, auto-resized to 200×200.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadIcon />}
                    component="label"
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Choose Photo
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                  </Button>
                  {formData.photo && (
                    <Button size="small" color="error" sx={{ ml: 1, textTransform: 'none' }}
                      onClick={() => setFormData(prev => ({ ...prev, photo: '' }))}>
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="primary" fontWeight={600}>Personal Information</Typography><Divider sx={{ mt: 1 }} /></Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="First Name" value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Last Name" value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Date of Birth" type="date" value={formData.dateOfBirth}
                onChange={handleDobChange}
                InputLabelProps={{ shrink: true }}
                error={!!ageError} helperText={ageError} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} label="Gender">
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Blood Group</InputLabel>
                <Select value={formData.bloodGroup} onChange={(e) => setFormData({...formData, bloodGroup: e.target.value})} label="Blood Group">
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                    <MenuItem key={bg} value={bg}>{bg}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Aadhaar Number" value={formData.aadharNumber}
                onChange={handleAadharChange}
                inputProps={{ maxLength: 12, inputMode: 'numeric', pattern: '[0-9]*' }}
                placeholder="12-digit Aadhaar No."
                error={!!aadharError}
                helperText={aadharError || `${formData.aadharNumber.length}/12 digits`} />
            </Grid>
            {/* Caste Details */}
            <Grid item xs={12}><Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mt: 1 }}>Caste Details</Typography><Divider sx={{ mt: 1 }} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Caste</InputLabel>
                <Select value={formData.caste} label="Caste" onChange={e => setFormData({...formData, caste: e.target.value, subCaste: '', category: ''})}>
                  <MenuItem value=""><em>— Select Caste —</em></MenuItem>
                  {[...new Set(casteList.map(c => c.casteName))].map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!formData.caste}>
                <InputLabel>Sub-Caste</InputLabel>
                <Select value={formData.subCaste} label="Sub-Caste" onChange={e => {
                  const entry = casteList.find(c => c.casteName === formData.caste && c.subCasteName === e.target.value);
                  setFormData({...formData, subCaste: e.target.value, category: entry?.category || formData.category});
                }}>
                  <MenuItem value=""><em>— Select Sub-Caste —</em></MenuItem>
                  {casteList.filter(c => c.casteName === formData.caste && c.subCasteName).map(c => (
                    <MenuItem key={c.id} value={c.subCasteName}>{c.subCasteName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Reservation Category</InputLabel>
                <Select value={formData.category} label="Reservation Category" onChange={e => setFormData({...formData, category: e.target.value})}>
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  <MenuItem value="GENERAL">General</MenuItem>
                  <MenuItem value="OBC">OBC</MenuItem>
                  <MenuItem value="SC">SC</MenuItem>
                  <MenuItem value="ST">ST</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mt: 1 }}>Contact Information</Typography><Divider sx={{ mt: 1 }} /></Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Phone" value={formData.phone}
                onChange={handlePhoneChange}
                inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                error={!!phoneError}
                helperText={phoneError || `${formData.phone.length}/10 digits`} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" type="email" value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" multiline rows={2} value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})} />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mt: 1 }}>Academic Information</Typography><Divider sx={{ mt: 1 }} /></Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Program / Year</InputLabel>
                <Select value={formData.schoolClass} onChange={(e) => setFormData({...formData, schoolClass: e.target.value, section: ''})} label="Program / Year">
                  <MenuItem value="">-- None --</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Batch</InputLabel>
                <Select value={formData.section} onChange={(e) => setFormData({...formData, section: e.target.value})} label="Batch" disabled={!formData.schoolClass}>
                  <MenuItem value="">-- None --</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Roll No" value={formData.rollNo}
                onChange={(e) => setFormData({...formData, rollNo: e.target.value})} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Admission Date" type="date" value={formData.admissionDate}
                onChange={(e) => setFormData({...formData, admissionDate: e.target.value})}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select value={formData.academicYearId || ''}
                  onChange={(e) => setFormData({...formData, academicYearId: e.target.value})}
                  label="Academic Year">
                  <MenuItem value="">-- None --</MenuItem>
                  {academicYears.map(y => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.name}{y.isActive ? ' ✓ Active' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Academic Year Fees (₹)" type="number" value={formData.academicYearFees}
                onChange={(e) => { setFormData({...formData, academicYearFees: e.target.value}); setAutoFeeInfo(null); }}
                inputProps={{ min: 0, step: 0.01 }} placeholder="e.g. 15000"
                helperText={autoFeeInfo ? 'Auto-filled from fee structure' : 'Enter manually if no structure'} />
            </Grid>

            {!isEdit && autoFeeInfo && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#e0f2f1', borderRadius: 2, border: '1px dashed #00796b' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#00796b" sx={{ mb: 1.5 }}>
                    📋 Fee Structure Auto-detected — {classes.find(c => String(c.id) === String(formData.schoolClass))?.name}
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      ['Tuition Fee', autoFeeInfo.tuitionFee],
                      ['Admission Fee', autoFeeInfo.admissionFee],
                      ['Exam Fee', autoFeeInfo.examFee],
                      ['Transport Fee', autoFeeInfo.transportFee],
                      ['Library Fee', autoFeeInfo.libraryFee],
                      ['Lab Fee', autoFeeInfo.labFee],
                      ['Sports Fee', autoFeeInfo.sportsFee],
                      ['Other Fee', autoFeeInfo.otherFee],
                    ].filter(([, v]) => parseFloat(v || 0) > 0).map(([label, value]) => (
                      <Grid item xs={6} sm={3} key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={600} color="#004d40">₹{parseFloat(value).toLocaleString()}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #80cbc4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="#00796b" fontWeight={800}>Total Annual Fee: ₹{parseFloat(autoFeeInfo.totalFee).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Auto-filled above ↑</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {!isEdit && (<>
              <Grid item xs={12}><Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mt: 1 }}>Guardian / Guardian Information</Typography><Divider sx={{ mt: 1 }} /></Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Father's Name" value={formData.fatherName}
                  onChange={(e) => setFormData({...formData, fatherName: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Father's Phone" value={formData.fatherPhone}
                  onChange={(e) => setFormData({...formData, fatherPhone: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Father's Email" type="email" value={formData.fatherEmail}
                  onChange={(e) => setFormData({...formData, fatherEmail: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Father's Occupation" value={formData.fatherOccupation}
                  onChange={(e) => setFormData({...formData, fatherOccupation: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Mother's Name" value={formData.motherName}
                  onChange={(e) => setFormData({...formData, motherName: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Mother's Phone" value={formData.motherPhone}
                  onChange={(e) => setFormData({...formData, motherPhone: e.target.value})} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Emergency Contact" value={formData.emergencyContact}
                  onChange={(e) => setFormData({...formData, emergencyContact: e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#e0f2f1', borderRadius: 2, border: '1px solid #00796b' }}>
                  <Typography variant="subtitle2" color="primary.dark" fontWeight={700} sx={{ mb: 1 }}>
                    Student App Login
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    The student uses this username and password to sign in to the Student mobile app. You can also create or reset it later from the student list.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Student Username" value={formData.studentUsername}
                        onChange={(e) => setFormData({...formData, studentUsername: e.target.value})}
                        placeholder="e.g., the admission number" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Student Password" type="password" value={formData.studentPassword}
                        onChange={(e) => setFormData({...formData, studentPassword: e.target.value})}
                        placeholder="Min 6 characters" />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Guardian App Login (optional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Only needed if a guardian should also follow the student's progress in the app.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Guardian Username" value={formData.parentUsername}
                        onChange={(e) => setFormData({...formData, parentUsername: e.target.value})}
                        placeholder="e.g., rajesh.kumar" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Guardian Password" type="password" value={formData.parentPassword}
                        onChange={(e) => setFormData({...formData, parentPassword: e.target.value})}
                        placeholder="Min 6 characters" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Guardian Full Name" value={formData.parentFullName}
                        onChange={(e) => setFormData({...formData, parentFullName: e.target.value})} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Guardian Phone" value={formData.parentPhone}
                        onChange={(e) => setFormData({...formData, parentPhone: e.target.value})} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Guardian Email" type="email" value={formData.parentEmail}
                        onChange={(e) => setFormData({...formData, parentEmail: e.target.value})} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </>)}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button onClick={handleCloseDialog} variant="outlined">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{isEdit ? 'Update Student' : 'Register Admission'}</Button>
        </DialogActions>
      </Dialog>

      {/* Student app login dialog */}
      <Dialog open={!!loginDialog} onClose={() => setLoginDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Student app login</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {loginDialog && (
            <>
              <Typography sx={{ mb: 2, fontWeight: 600 }}>
                {loginDialog.student.firstName} {loginDialog.student.lastName} ({loginDialog.student.admissionNo})
              </Typography>
              <TextField fullWidth label="Username" sx={{ mb: 2 }} value={loginDialog.username}
                onChange={(e) => setLoginDialog({ ...loginDialog, username: e.target.value })} />
              <TextField fullWidth label="New password" type="password" value={loginDialog.password}
                onChange={(e) => setLoginDialog({ ...loginDialog, password: e.target.value })}
                helperText="At least 6 characters. Saving creates the login, or resets it if the student already has one." />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLoginDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLogin}>Save login</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>Student Details</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedStudent && (
            <Grid container spacing={2}>
              <Grid item xs={12} textAlign="center">
                <Avatar
                  src={selectedStudent.photo || undefined}
                  sx={{ width: 90, height: 90, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 28, border: '3px solid #00897b' }}
                >
                  {!selectedStudent.photo && `${selectedStudent.firstName?.[0] || ''}${selectedStudent.lastName?.[0] || ''}`}
                </Avatar>
                <Typography variant="h6">{selectedStudent.firstName} {selectedStudent.lastName}</Typography>
                <Chip label={selectedStudent.admissionNo} color="primary" size="small" sx={{ mt: 1 }} />
              </Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Program / Year</Typography><Typography>{selectedStudent.schoolClass?.name || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Batch</Typography><Typography>{selectedStudent.section?.name || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Roll No</Typography><Typography>{selectedStudent.rollNo || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Gender</Typography><Typography>{selectedStudent.gender || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Date of Birth</Typography><Typography>{selectedStudent.dateOfBirth || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Blood Group</Typography><Typography>{selectedStudent.bloodGroup || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Phone</Typography><Typography>{selectedStudent.phone || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Email</Typography><Typography>{selectedStudent.email || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="body2" color="text.secondary">Address</Typography><Typography>{selectedStudent.address || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Aadhaar Number</Typography><Typography>{selectedStudent.aadharNumber || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2" color="text.secondary">Academic Year Fees</Typography><Typography>{selectedStudent.academicYearFees != null ? `₹${selectedStudent.academicYearFees}` : '-'}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenViewDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete student <strong>{selectedStudent?.firstName} {selectedStudent?.lastName}</strong>?</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
